import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon, CheckCircle2Icon, CreditCardIcon, Loader2Icon, PrinterIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { ErrorState } from '@/components/ErrorState';
import { LoadingBlock } from '@/components/LoadingBlock';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { INVOICE_STATUS_LABEL, billingApi, fmtDay, fmtMoney } from '@/lib/billing';
import { fmtDateTime } from '@/lib/format';
import { BILLING_KEY } from './BillingPage';

/** One invoice, printable, with its payments (receipts) and a Pay button while it is open. */
export function InvoicePage() {
  const { invoiceId = '' } = useParams<{ invoiceId: string }>();
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const { refreshMe } = useAuth();
  const q = useQuery({ queryKey: [...BILLING_KEY, 'invoice', invoiceId], queryFn: () => billingApi.invoice(invoiceId), enabled: !!invoiceId });
  const [saveCard, setSaveCard] = useState(false);
  const [paying, setPaying] = useState(false);

  // Back from the payment provider.
  useEffect(() => {
    if (params.get('paid') === '1') toast.success('Thank you. Your payment was received; the receipt is on its way.');
    else if (params.get('cancelled') === '1') toast('Payment cancelled. The invoice is still open.');
    else return;
    const next = new URLSearchParams(params);
    next.delete('paid');
    next.delete('cancelled');
    setParams(next, { replace: true });
    void qc.invalidateQueries({ queryKey: BILLING_KEY });
    void refreshMe();
  }, [params, setParams, qc, refreshMe]);

  if (q.isPending) return <LoadingBlock rows={6} />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  const { invoice, billedTo, company } = q.data;
  const overdue = invoice.status === 'open' && (invoice.daysOverdue ?? 0) > 0;

  const pay = async () => {
    setPaying(true);
    try {
      const { url } = await billingApi.pay(invoice.id, saveCard);
      window.location.assign(url);
    } catch (err) {
      toast.error(errorMessage(err));
      setPaying(false);
    }
  };

  return (
    <div className="space-y-4 print:space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <Button asChild variant="ghost" size="sm">
          <Link to="/org/billing">
            <ArrowLeftIcon aria-hidden /> Billing
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <PrinterIcon aria-hidden /> Print / save PDF
          </Button>
        </div>
      </div>

      <article className="bg-card mx-auto max-w-3xl border p-6 sm:p-10 print:border-0 print:p-0">
        <header className="flex flex-wrap items-start justify-between gap-6 border-b pb-6">
          <div>
            <div className="text-lg font-semibold">{company.name}</div>
            <div className="text-muted-foreground text-sm">
              {company.addressLines.map((l) => (
                <div key={l}>{l}</div>
              ))}
              {company.email ? <div>{company.email}</div> : null}
              {company.phone ? <div>{company.phone}</div> : null}
              {company.taxId ? <div>Tax ID {company.taxId}</div> : null}
            </div>
          </div>
          <div className="text-right">
            <div className="text-muted-foreground text-xs tracking-[0.18em] uppercase">{invoice.status === 'paid' ? 'Paid invoice' : 'Invoice'}</div>
            <div className="text-2xl font-semibold">{invoice.number}</div>
            <div className="mt-2 flex justify-end">
              <Badge variant={invoice.status === 'paid' ? 'success' : overdue ? 'danger' : invoice.status === 'open' ? 'warning' : 'muted'}>{overdue ? `Overdue · ${invoice.daysOverdue}d` : INVOICE_STATUS_LABEL[invoice.status]}</Badge>
            </div>
          </div>
        </header>

        <section className="grid gap-6 py-6 sm:grid-cols-3">
          <div className="sm:col-span-1">
            <div className="text-muted-foreground text-xs tracking-[0.18em] uppercase">Billed to</div>
            <div className="mt-1 text-sm font-medium">{billedTo.name}</div>
            <div className="text-muted-foreground text-sm">
              {billedTo.address.line1 ? <div>{billedTo.address.line1}</div> : null}
              {billedTo.address.line2 ? <div>{billedTo.address.line2}</div> : null}
              {billedTo.address.city || billedTo.address.state || billedTo.address.postalCode ? <div>{[billedTo.address.city, billedTo.address.state, billedTo.address.postalCode].filter(Boolean).join(', ')}</div> : null}
              {billedTo.email ? <div>{billedTo.email}</div> : null}
              {billedTo.taxId ? <div>Tax ID {billedTo.taxId}</div> : null}
            </div>
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:col-span-2 sm:grid-cols-4">
            <dt className="text-muted-foreground">Issued</dt>
            <dd>{fmtDay(invoice.issuedOn)}</dd>
            <dt className="text-muted-foreground">Due</dt>
            <dd>{fmtDay(invoice.dueOn)}</dd>
            {invoice.periodStart && invoice.periodEnd ? (
              <>
                <dt className="text-muted-foreground">Service period</dt>
                <dd className="sm:col-span-3">
                  {fmtDay(invoice.periodStart)} – {fmtDay(shiftDay(invoice.periodEnd, -1))}
                </dd>
              </>
            ) : null}
            {invoice.memo ? (
              <>
                <dt className="text-muted-foreground">Memo</dt>
                <dd className="sm:col-span-3">{invoice.memo}</dd>
              </>
            ) : null}
          </dl>
        </section>

        <table className="w-full text-sm">
          <thead>
            <tr className="text-muted-foreground border-b text-left text-xs tracking-wide uppercase">
              <th className="py-2 font-medium">Description</th>
              <th className="py-2 text-right font-medium">Qty</th>
              <th className="py-2 text-right font-medium">Unit</th>
              <th className="py-2 text-right font-medium">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(invoice.lines ?? []).map((l) => (
              <tr key={l.id} className="border-b">
                <td className="py-2 pr-4">{l.description}</td>
                <td className="py-2 text-right tabular-nums">{l.quantity}</td>
                <td className="py-2 text-right tabular-nums">{fmtMoney(l.unitAmountCents, invoice.currency)}</td>
                <td className="py-2 text-right tabular-nums">{fmtMoney(l.amountCents, invoice.currency)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3} className="text-muted-foreground pt-3 text-right">
                Subtotal
              </td>
              <td className="pt-3 text-right tabular-nums">{fmtMoney(invoice.subtotalCents, invoice.currency)}</td>
            </tr>
            {invoice.taxCents > 0 ? (
              <tr>
                <td colSpan={3} className="text-muted-foreground text-right">
                  Tax
                </td>
                <td className="text-right tabular-nums">{fmtMoney(invoice.taxCents, invoice.currency)}</td>
              </tr>
            ) : null}
            <tr className="font-semibold">
              <td colSpan={3} className="pt-1 text-right">
                Total
              </td>
              <td className="pt-1 text-right tabular-nums">{fmtMoney(invoice.totalCents, invoice.currency)}</td>
            </tr>
            {invoice.amountPaidCents > 0 ? (
              <>
                <tr>
                  <td colSpan={3} className="text-muted-foreground text-right">
                    Paid
                  </td>
                  <td className="text-right tabular-nums">−{fmtMoney(invoice.amountPaidCents, invoice.currency)}</td>
                </tr>
                <tr className="font-semibold">
                  <td colSpan={3} className="text-right">
                    Amount due
                  </td>
                  <td className="text-right tabular-nums">{fmtMoney(invoice.amountDueCents, invoice.currency)}</td>
                </tr>
              </>
            ) : null}
          </tfoot>
        </table>

        {invoice.payments && invoice.payments.length > 0 ? (
          <section className="mt-6 border-t pt-4">
            <div className="text-muted-foreground text-xs tracking-[0.18em] uppercase">Payments</div>
            <ul className="mt-2 space-y-1 text-sm">
              {invoice.payments.map((p) => (
                <li key={p.id} className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <CheckCircle2Icon className="text-success size-4" aria-hidden />
                    {fmtDateTime(p.receivedAt)} · {p.method.replace(/_/g, ' ')}
                    {p.receiptNumber ? <span className="text-muted-foreground">· receipt {p.receiptNumber}</span> : null}
                    {p.reference ? <span className="text-muted-foreground">· ref {p.reference}</span> : null}
                  </span>
                  <span className="tabular-nums">
                    {fmtMoney(p.amountCents, p.currency)}
                    {p.refundedCents > 0 ? <span className="text-muted-foreground"> (refunded {fmtMoney(p.refundedCents, p.currency)})</span> : null}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {invoice.footer ? <p className="text-muted-foreground mt-8 border-t pt-4 text-xs">{invoice.footer}</p> : null}
      </article>

      {invoice.status === 'open' && invoice.amountDueCents > 0 ? (
        <div className="bg-card mx-auto flex max-w-3xl flex-wrap items-center justify-between gap-4 border p-4 print:hidden">
          <div>
            <div className="text-sm font-medium">{fmtMoney(invoice.amountDueCents, invoice.currency)} due {fmtDay(invoice.dueOn)}</div>
            <p className="text-muted-foreground text-xs">{invoice.collectionMethod === 'charge_automatically' ? 'This invoice is charged to your saved payment method automatically. You can also pay it now.' : 'Pay securely by card. You will be sent to our payment provider and back.'}</p>
            <div className="mt-2 flex items-center gap-2">
              <Checkbox id="save-card" checked={saveCard} onCheckedChange={(v) => setSaveCard(v === true)} />
              <Label htmlFor="save-card" className="text-muted-foreground cursor-pointer text-xs font-normal">
                Save this card for future invoices
              </Label>
            </div>
          </div>
          <Button size="lg" onClick={() => void pay()} disabled={paying}>
            {paying ? <Loader2Icon className="animate-spin" aria-hidden /> : <CreditCardIcon aria-hidden />} Pay {fmtMoney(invoice.amountDueCents, invoice.currency)}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function shiftDay(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
